import React, { useContext, useEffect } from 'react'
import { useMediaQuery } from 'react-responsive'

import { useMutation, useQuery } from '@apollo/react-hooks'
import { useFormik } from 'formikV2'
import { compose } from 'recompose'

import CenteredSpinner from 'components/CenteredSpinner'
import withHeroText, { HeroTextProps } from 'components/HOCs/withHeroText'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import platform from 'components/Trackers/platform'
import {
  ActionType,
  MutationSetManualRoundUpsArgs,
  PageName,
  RoundUp,
  RoundUpTypePagination,
} from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import theme from 'theme'
import trackEvent from 'utils/analytics/trackEvent'
import { withApolloErrorModal } from 'utils/Apollo'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getFlow } from 'utils/FlowProvider/utils'
import { FunnelContext } from 'utils/FunnelContext'
import { withPrompt } from 'utils/prompts'
import withTieredRegistration, { WithTieredRegistrationProps } from 'utils/TieredRegistration'

import { handleSubmit as handleFormSubmit } from './form'
import setManualRoundupsMutation from './gql/mutation.setManualRoundups.gql'
import roundupTransactionsQuery from './gql/query.roundupTransactions.gql'
import {
  ActionWrapper,
  Container,
  FormRow,
  Grid,
  Label,
  RoundupCost,
  StyledBody,
  StyledButton,
  StyledCurrencyInput,
  StyledIcon,
  StyledLayout,
  TotalCost,
  TransactionTitle,
} from './styled-components'

type Props = {
  handleNextClick: () => void
  header: string
  text: string
} & HeroTextProps &
  WithTieredRegistrationProps &
  WithFlowRouterProps

type FormOption = {
  name: string
  id: string
  change: string
  totalCost: string
  value: boolean
}

type RoundUpsList = {
  roundUps: RoundUpTypePagination
}

type SetManualRoundUpsResults = {
  roundUp: RoundUp
}

type SetManualRoundUpsInput = MutationSetManualRoundUpsArgs

const RoundUpsPastScreenViewedEvent = {
  feature: 'Registration: Round-Ups Past Screen',
  event_description: 'Screen Viewed',
  platform,
  object: 'Screen',
  action: 'Viewed',
  object_name: 'registrationRoundUpsPast',
  style: 'main',
  screen: 'registrationRoundUpsPast',
  screen_name: 'registrationRoundUpsPast',
}

const NextButtonTappedEvent = {
  feature: 'Registration: Round-Ups Past',
  event_description: 'Next button tapped',
  platform,
  object: 'Button',
  action: 'Tapped',
  object_name: 'registrationRoundUpsPast',
  screen: 'registrationRoundUpsPast',
  screen_name: 'registrationRoundUpsPast',
}

const trackRoundUpsPastButtonTapped = () => {
  trackEvent('Button Tapped', {
    eventData: NextButtonTappedEvent,
    flow: getFlow(),
  })
}

const enhance = compose<Props, {}>(
  withHeroText,
  withApolloErrorModal,
  withFlowRouter,
  withPrompt,
  withTieredRegistration,
)

const RoundupsPast: React.FunctionComponent<Props> = ({
  closeErrorModal,
  heroText,
  promptUser,
  route,
  showErrorModal,
}) => {
  const { isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const [setManualRoundups] = useMutation<SetManualRoundUpsResults, SetManualRoundUpsInput>(
    setManualRoundupsMutation,
  )
  const { data: roundupsData, loading } = useQuery<RoundUpsList>(roundupTransactionsQuery, {
    fetchPolicy: 'network-only',
  })

  const isDesktop = useMediaQuery({ minWidth: `${theme.breakpoints.md}px` })

  useEffect(() => {
    trackEvent('Screen Viewed', {
      eventData: RoundUpsPastScreenViewedEvent,
      flow: getFlow(),
    })
  }, [])

  let formOptions: FormOption[] = []

  // @note the overall sum of all transactions (transaction.node.change) returned by roundupTransaction query
  let overallTransactionChangeTotal = 0

  // @note the minimum transaction amount (transaction.node.change) closest to $5
  let minTransactionChangeTotal = 0

  // grab the sum of all values in roundupTransactions array
  const reducer = (prev, curr) => prev + curr

  const roundUpTransactions = roundupsData?.roundUps?.edges

  overallTransactionChangeTotal = roundUpTransactions
    ?.map(transaction => transaction?.node?.change)
    ?.reduce(reducer, 0)

  formOptions = roundUpTransactions?.map(transaction => {
    if (minTransactionChangeTotal < 5 && transaction?.node?.transactionId) {
      const currentTransactionChange = parseFloat(transaction?.node?.change.toFixed(2))
      minTransactionChangeTotal += currentTransactionChange
      return {
        name: transaction?.node?.name || '',
        id: transaction?.node.transactionId || '',
        change: transaction?.node?.change.toFixed(2) || '0',
        totalCost: transaction?.node?.amount.toFixed(2) || '0',
        value: false,
      }
    }
  })

  const handleSkip = () => {
    isDynamicFunnelEnabled
      ? routeToPage(PageName.RoundUpsPast, ActionType.Continue)
      : route({ from: FlowRoute.RoundUpsPast, action: 'continue' })
  }

  const { handleSubmit } = useFormik({
    onSubmit: handleFormSubmit({
      closeErrorModal,
      options: formOptions?.map(option => option?.id),
      promptUser,
      isDynamicFunnelEnabled,
      route,
      routeToPage,
      showErrorModal,
      setManualRoundups,
      trackRoundUpsPastButtonTapped,
    }),
    initialValues: formOptions || [],
  })

  if (loading) {
    return <CenteredSpinner />
  }

  // @note if discovered possible RoundUps are less than $5, we will redirect user to next chapter in flow
  if (!loading && overallTransactionChangeTotal < 5) {
    handleSkip()
  }

  return (
    <>
      <Nav back={true} linkText="Skip" onLink={handleSkip} />
      <StyledLayout>
        <Container>
          {isDesktop ? (
            <StyledBody>{heroText.text}</StyledBody>
          ) : (
            <>
              <StyledCurrencyInput data-testid={TestIDs.ROUNDUPS_PAST.MOBILE_TOTAL_COST}>
                ${minTransactionChangeTotal.toFixed(2)}
              </StyledCurrencyInput>
              <StyledBody>{heroText.mobileText}</StyledBody>
            </>
          )}
          <Grid>
            {formOptions?.map(option => {
              if (option) {
                return (
                  <FormRow key={option.id} data-testid={option.id}>
                    <StyledIcon />
                    <RoundupCost>${option.change}</RoundupCost>
                    <Label>
                      <TransactionTitle>{option.name}</TransactionTitle>
                      <TotalCost>${option.totalCost}</TotalCost>
                    </Label>
                  </FormRow>
                )
              }
            })}
          </Grid>
          <ActionWrapper>
            <Separator />
            <StyledButton
              data-testid={TestIDs.ROUNDUPS_PAST.NEXT_BUTTON}
              onPress={handleSubmit}
              disabled={loading}
            >
              {heroText.ctaLabel}
              {minTransactionChangeTotal ? minTransactionChangeTotal.toFixed(2) : '0'}
            </StyledButton>
          </ActionWrapper>
        </Container>
      </StyledLayout>
    </>
  )
}

export default enhance(RoundupsPast)
